import { useState } from 'react';
import {
  Box, Typography, Button, List, ListItem, ListItemText, ListItemIcon,
  ListItemSecondaryAction, IconButton, Chip, Paper, Stack, Divider,
  CircularProgress, Badge,
} from '@mui/material';
import {
  Notifications as NotificationsIcon, NotificationsNone,
  CheckCircle, Delete, DoneAll, Payment, Warning, Info,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@smartgrow/shared';
import type { Notification } from '@smartgrow/shared';

const typeIcon = (type: string) => {
  switch (type) {
    case 'PAYMENT_COMPLETED': return <Payment color="success" />;
    case 'SALARY_PAYMENT_DUE': return <Warning color="warning" />;
    case 'PAYMENT_REQUEST_APPROVED': return <CheckCircle color="success" />;
    case 'PAYMENT_REQUEST_REJECTED': return <Warning color="error" />;
    default: return <Info color="info" />;
  }
};

const typeColor = (type: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (type) {
    case 'PAYMENT_COMPLETED': case 'PAYMENT_REQUEST_APPROVED': return 'success';
    case 'SALARY_PAYMENT_DUE': return 'warning';
    case 'PAYMENT_REQUEST_REJECTED': return 'error';
    default: return 'info';
  }
};

const fmtDate = (s: string) => {
  const d = new Date(s);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-NG');
};

export const NotificationsPage = () => {
  const [filter, setFilter] = useState<'ALL' | 'UNREAD'>('ALL');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', filter],
    queryFn: () =>
      filter === 'UNREAD'
        ? notificationService.getUnread().then((list) => ({ content: list, totalElements: list.length }))
        : notificationService.getAll(),
  });

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-count'],
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 30000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });

  const deleteNotif = useMutation({
    mutationFn: (id: string) => notificationService.deleteNotification(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const notifications: Notification[] = (data as any)?.content ?? data ?? [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
          <Typography variant="h5" fontWeight="bold">Notifications</Typography>
        </Stack>
        <Button
          startIcon={<DoneAll />}
          variant="outlined"
          size="small"
          onClick={() => markAllRead.mutate()}
          disabled={unreadCount === 0 || markAllRead.isPending}
        >
          Mark all read
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip
          label="All"
          onClick={() => setFilter('ALL')}
          color={filter === 'ALL' ? 'primary' : 'default'}
          variant={filter === 'ALL' ? 'filled' : 'outlined'}
        />
        <Chip
          label={`Unread (${unreadCount})`}
          onClick={() => setFilter('UNREAD')}
          color={filter === 'UNREAD' ? 'primary' : 'default'}
          variant={filter === 'UNREAD' ? 'filled' : 'outlined'}
        />
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress />
        </Box>
      ) : notifications.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <NotificationsNone sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">
            {filter === 'UNREAD' ? 'No unread notifications' : 'No notifications yet'}
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {notifications.map((n, idx) => (
              <Box key={n.id}>
                {idx > 0 && <Divider />}
                <ListItem
                  sx={{
                    bgcolor: n.isRead ? 'transparent' : 'action.hover',
                    pr: 12,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {typeIcon(n.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={n.isRead ? 'normal' : 'bold'}>
                          {n.title}
                        </Typography>
                        <Chip
                          label={n.type.replace(/_/g, ' ')}
                          size="small"
                          color={typeColor(n.type)}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          {n.message}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {fmtDate(n.createdAt)}
                        </Typography>
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={0.5}>
                      {!n.isRead && (
                        <IconButton
                          size="small"
                          onClick={() => markRead.mutate(n.id)}
                          title="Mark as read"
                        >
                          <CheckCircle fontSize="small" color="success" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => deleteNotif.mutate(n.id)}
                        title="Delete"
                      >
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              </Box>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};
